import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    makeStyles,
    Typography,
} from '@material-ui/core';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import commonStyles from 'config/commonStyles';
import tableMapsStore from 'store/tableMapsStore';
import {
    errorMessage,
    isFeatureSaveError,
    QrConflictError,
    TableSaveError,
} from 'store/tableMapsStore/actionCreators';
import globalStore from 'store/globalStore';
import { TableMapDetails } from 'store/tableMapsStore/reducer';
import { ApplicationState } from 'store';
import { PopupConfirmation } from 'components/shared';
import { DineInQrCode, TableMaps } from 'api/controllers/tableMaps';
import { releaseUsedQrCodes } from './tableEdit';
import {
    canApplyPendingTable,
    canSaveDraft,
    draftFromDetails,
    hasUncommittedEdits,
    isStepValid,
    TableMapWizardProvider,
    useTableMapWizard,
} from './context';
import WizardStepper from './components/WizardStepper';
import BasicInformationStep from './steps/BasicInformationStep';
import MapConfigurationStep from './steps/MapConfigurationStep';
import RoomFeaturesStep from './steps/RoomFeaturesStep';
import TablePlacementStep from './steps/TablePlacementStep';
import TableDetailsStep from './steps/TableDetailsStep';
import ReviewStep, { isReviewReady } from './steps/ReviewStep';
import CreatedStep, { CreatedResult } from './steps/CreatedStep';
import { OPENING_COLORS } from './components/MapEditorCanvas';

const TOTAL_STEPS = 7;

type StepExit = 'next' | 'back' | 'saveDraft' | number;

const useStyles = makeStyles({
    ...commonStyles,
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        padding: '6px 16px 0',
        backgroundColor: theme.palette.common.white,
        paddingBottom: 16,
        borderBottom: `1px solid ${theme.palette.grey['300']}`,
    },
    headerTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.primary.dark,
        lineHeight: 1.2,
    },
    headerSubtitle: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    body: {
        flex: 1,
        overflow: 'auto',
        padding: '24px 16px',
    },
    canvasBody: {
        display: 'flex',
        flexDirection: 'column',
    },
    canvasStepHost: {
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column',
    },
    pendingDialog: {
        '& div.MuiDialog-paper': {
            padding: '18px 24px 0 24px',
            width: 468,
        },
        // The theme gives dialog actions a bottom margin and no padding; PopupConfirmation resets both the same way.
        '& div': {
            margin: 0,
            padding: 0,
        },
    },
    pendingDialogText: {
        color: theme.palette.common.black,
        margin: '20px 0',
    },
    backButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    cancelButton: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    saveDraftButton: {
        color: OPENING_COLORS.Entrance,
        borderColor: OPENING_COLORS.Entrance,
        fontWeight: 600,
        letterSpacing: 1.25,
    },
    nextButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        fontWeight: 600,
        letterSpacing: 1.25,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
        },
    },
    loadingRoot: {
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.common.white,
    },
});

interface WizardShellProps {
    isEdit: boolean;
    editId?: string;
    originalDetails?: TableMapDetails;
    releasedQrTables?: string[];
}

const NO_TABLES: TableMapDetails['tables'] = [];

function WizardShell({ isEdit, editId, originalDetails, releasedQrTables = [] }: WizardShellProps) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { draft, dispatch: wizardDispatch } = useTableMapWizard();
    const userProfile = useSelector((state: ApplicationState) => state.user.profile);
    const [stepIndex, setStepIndex] = useState(originalDetails?.draftStep ?? 0);
    const [saving, setSaving] = useState(false);
    const [createdResult, setCreatedResult] = useState<CreatedResult | null>(null);
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
    const [initialDraft] = useState(draft);
    const [draftSaving, setDraftSaving] = useState(false);
    const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
    const [queuedExit, setQueuedExit] = useState<StepExit | null>(null);
    const isLeavingRef = useRef(false);

    const currentStep = stepIndex + 1;
    const isCanvasStep = stepIndex >= 1 && stepIndex <= 5;
    const canAdvance = isStepValid(stepIndex, draft);
    const isDraftResume = !!originalDetails?.isDraft;
    const isPublishedEdit = isEdit && !isDraftResume;
    const pendingTableName = draft.tables.find((t) => t.id === draft.pendingTable?.id)?.label ?? '';
    const showSaveDraft = isDraftResume || (!isEdit && stepIndex >= 1 && stepIndex <= 4);
    const hasUnsavedChanges = draft !== initialDraft && stepIndex !== 6;
    const mapFields = {
        name: draft.name,
        isActive: draft.isActive,
        entrances: draft.entrances,
        features: draft.features,
        size: draft.size,
        area: draft.area,
        tables: draft.tables,
        gridEnabled: draft.gridEnabled,
    };

    useEffect(() => {
        if (!hasUnsavedChanges) return undefined;
        function handleBeforeUnload(e: BeforeUnloadEvent) {
            e.preventDefault();
            e.returnValue = '';
        }
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Runs a queued exit once the pending table edit is applied or discarded, against the new draft.
    useEffect(() => {
        if (queuedExit === null || draft.pendingTable) return;
        setQueuedExit(null);
        runExit(queuedExit);
    });

    function leaveTo(path: string) {
        isLeavingRef.current = true;
        history.push(path);
    }

    function runExit(exit: StepExit) {
        if (exit === 'next') handleNext();
        else if (exit === 'back') handleBack();
        else if (exit === 'saveDraft') handleSaveDraft();
        else setStepIndex(exit);
    }

    function requestExit(exit: StepExit) {
        if (draft.pendingTable) setQueuedExit(exit);
        else runExit(exit);
    }

    function handleNext() {
        if (stepIndex < TOTAL_STEPS - 1 && canAdvance) {
            setStepIndex((prev) => prev + 1);
        }
    }

    function handleBack() {
        if (stepIndex > 0 && !hasUncommittedEdits(draft)) {
            setStepIndex((prev) => prev - 1);
        }
    }

    function handleCancel() {
        setCancelConfirmOpen(true);
    }

    function handleCancelConfirmed() {
        leaveTo('/table-maps');
    }

    function saveErrorMessage(err: unknown): string | null {
        if (err instanceof QrConflictError) {
            return i18n.t('tableMaps.wizard.review.qrConflict', {
                tables: err.qrConflictLabels.join(', '),
            });
        }
        if (err instanceof TableSaveError) {
            return err.serverMessage ?? i18n.t('tableMaps.wizard.tables.saveError');
        }
        return isFeatureSaveError(err)
            ? i18n.t('tableMaps.wizard.features.saveError')
            : errorMessage(err);
    }

    function showSaveError(err: unknown, fallback: string) {
        dispatch(
            globalStore.actionCreators.showToaster('error', saveErrorMessage(err) ?? fallback),
        );
    }

    async function handleSaveDraft() {
        setDraftSaving(true);
        try {
            const draftState = { isDraft: true, draftStep: stepIndex };
            const thunk =
                editId && originalDetails
                    ? tableMapsStore.actionCreators.updateTableMap({
                          ...mapFields,
                          ...draftState,
                          id: editId,
                          originalTables: originalDetails.tables,
                      })
                    : tableMapsStore.actionCreators.createTableMap({
                          ...mapFields,
                          ...draftState,
                      });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (dispatch(thunk as any) as Promise<unknown>);
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('tableMaps.wizard.draftSaved'),
                ),
            );
            leaveTo('/table-maps');
        } catch (err) {
            if (editId) {
                dispatch(tableMapsStore.actionCreators.getTableMapDetails(editId) as never);
            }
            setDraftSaving(false);
            setDraftSaveError(saveErrorMessage(err) ?? '');
        }
    }

    async function handleCreate() {
        setSaving(true);
        try {
            const createdAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.createTableMap({
                ...mapFields,
                isDraft: false,
                draftStep: null,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const createdId = await (dispatch(thunk as any) as Promise<string>);

            setCreatedResult({
                qrReleasedTables: [],
                id: createdId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: draft.isActive ? 'Active' : 'Inactive',
                isActive: draft.isActive,
                createdAt,
                createdBy,
            });
            setStepIndex(6);
        } catch (err) {
            showSaveError(err, i18n.t('tableMaps.wizard.review.createError'));
        } finally {
            setSaving(false);
        }
    }

    async function handleUpdate() {
        if (!editId || !originalDetails) return;
        setSaving(true);
        try {
            const updatedAt = new Date().toISOString();
            const nameParts = [userProfile.firstName, userProfile.lastName].filter(Boolean);
            const createdBy =
                nameParts.length > 0
                    ? nameParts.join(' ')
                    : userProfile.email || i18n.t('tableMaps.wizard.created.unknownUser');

            const thunk = tableMapsStore.actionCreators.updateTableMap({
                ...mapFields,
                id: editId,
                isDraft: false,
                draftStep: null,
                originalTables: originalDetails.tables,
                publishesDraft: isDraftResume,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const qrReleasedTables = await (dispatch(thunk as any) as Promise<string[]>);

            setCreatedResult({
                qrReleasedTables,
                id: editId,
                name: draft.name,
                tablesCount: draft.tables.length,
                totalSeats: draft.tables.reduce((sum, t) => sum + t.seats, 0),
                status: draft.isActive ? 'Active' : 'Inactive',
                isActive: draft.isActive,
                createdAt: updatedAt,
                createdBy,
            });
            setStepIndex(6);
        } catch (err) {
            showSaveError(err, i18n.t('tableMaps.wizard.review.saveError'));
            // Part of the save may have landed: the retry must diff against the server, not the stale snapshot.
            await (dispatch(
                tableMapsStore.actionCreators.getTableMapDetails(editId) as never,
            ) as unknown as Promise<void>);
        } finally {
            setSaving(false);
        }
    }

    function renderStepPanel() {
        if (stepIndex === 0) {
            return <BasicInformationStep />;
        }
        if (stepIndex === 1) {
            return <MapConfigurationStep />;
        }
        if (stepIndex === 2) {
            return <RoomFeaturesStep />;
        }
        if (stepIndex === 3) {
            return <TablePlacementStep />;
        }
        if (stepIndex === 4) {
            return (
                <TableDetailsStep
                    // On a draft `isUsed` never means "used by this map", so none of its codes is exempt.
                    originalTables={
                        isDraftResume ? NO_TABLES : (originalDetails?.tables ?? NO_TABLES)
                    }
                    releasedQrTables={releasedQrTables}
                />
            );
        }
        if (stepIndex === 5) {
            return <ReviewStep />;
        }
        if (stepIndex === 6 && createdResult) {
            return (
                <CreatedStep draft={draft} createdResult={createdResult} isEdit={isPublishedEdit} />
            );
        }
        return null;
    }

    const title = isPublishedEdit
        ? i18n.t('tableMaps.wizard.editTitle')
        : i18n.t('tableMaps.wizard.title');

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerTopRow}>
                    <div>
                        <Typography className={classes.headerTitle}>{title}</Typography>
                        <Typography className={classes.headerSubtitle}>
                            {[
                                stepIndex !== 6 &&
                                    i18n.t('tableMaps.wizard.stepOf', { n: currentStep }),
                                draft.name.trim(),
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                    </div>
                    <div className={classes.headerActions}>
                        {stepIndex === 6 ? (
                            <>
                                <Button
                                    variant="outlined"
                                    className={classes.backButton}
                                    onClick={() => history.push('/table-maps')}
                                >
                                    {i18n.t('tableMaps.wizard.created.backToTableMaps')}
                                </Button>
                                <Button
                                    variant="contained"
                                    className={classes.nextButton}
                                    disabled={!createdResult}
                                    onClick={() => {
                                        if (createdResult) {
                                            history.push(`/table-maps/${createdResult.id}`);
                                        }
                                    }}
                                >
                                    {i18n.t('tableMaps.wizard.created.openTableMap')}
                                </Button>
                            </>
                        ) : (
                            <>
                                {showSaveDraft && (
                                    <Button
                                        variant="outlined"
                                        className={classes.saveDraftButton}
                                        onClick={() => requestExit('saveDraft')}
                                        disabled={!canSaveDraft(draft) || draftSaving}
                                        startIcon={
                                            draftSaving ? <CircularProgress size={16} /> : undefined
                                        }
                                    >
                                        {i18n.t('tableMaps.wizard.saveDraft')}
                                    </Button>
                                )}
                                {stepIndex > 0 && (
                                    <Button
                                        variant="outlined"
                                        className={classes.backButton}
                                        onClick={() => requestExit('back')}
                                        disabled={hasUncommittedEdits(draft) || draftSaving}
                                    >
                                        {i18n.t('tableMaps.wizard.back')}
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    className={classes.cancelButton}
                                    onClick={handleCancel}
                                    disabled={draftSaving}
                                >
                                    {i18n.t('tableMaps.wizard.cancel')}
                                </Button>
                                {stepIndex < 5 && (
                                    <Button
                                        variant="contained"
                                        className={classes.nextButton}
                                        onClick={() => requestExit('next')}
                                        disabled={
                                            !(canAdvance || draft.pendingTable) || draftSaving
                                        }
                                    >
                                        {i18n.t('tableMaps.wizard.next')}
                                    </Button>
                                )}
                                {stepIndex === 5 && (
                                    <Button
                                        variant="contained"
                                        className={classes.nextButton}
                                        onClick={isEdit ? handleUpdate : handleCreate}
                                        disabled={
                                            !isReviewReady(draft) ||
                                            saving ||
                                            draftSaving ||
                                            hasUncommittedEdits(draft)
                                        }
                                    >
                                        {isPublishedEdit
                                            ? i18n.t('tableMaps.wizard.saveChanges')
                                            : i18n.t('tableMaps.wizard.review.createTableMap')}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <WizardStepper
                    currentStep={currentStep}
                    onStepClick={
                        isEdit && stepIndex !== 6 && !hasUncommittedEdits(draft) && !draftSaving
                            ? (target) => requestExit(target)
                            : undefined
                    }
                />
            </div>

            <div className={`${classes.body} ${isCanvasStep ? classes.canvasBody : ''}`}>
                <div className={isCanvasStep ? classes.canvasStepHost : undefined}>
                    {renderStepPanel()}
                </div>
            </div>

            <PopupConfirmation
                open={cancelConfirmOpen}
                close={() => setCancelConfirmOpen(false)}
                title={
                    isPublishedEdit
                        ? i18n.t('tableMaps.wizard.cancelEditTitle')
                        : i18n.t('tableMaps.wizard.cancelTitle')
                }
                description={i18n.t('tableMaps.wizard.cancelDescription')}
                activeBtn={i18n.t('tableMaps.wizard.cancelConfirm')}
                action={handleCancelConfirmed}
            />

            <PopupConfirmation
                open={draftSaveError !== null}
                close={() => setDraftSaveError(null)}
                title={i18n.t('tableMaps.wizard.draftSaveError')}
                description={draftSaveError ?? ''}
                activeBtn={i18n.t('tableMaps.wizard.retry')}
                action={handleSaveDraft}
            />

            <Dialog
                className={classes.pendingDialog}
                open={queuedExit !== null && !!draft.pendingTable}
                onClose={() => setQueuedExit(null)}
            >
                <DialogTitle>
                    {i18n.t('tableMaps.wizard.tables.pendingTitle', { name: pendingTableName })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText className={classes.pendingDialogText}>
                        {i18n.t('tableMaps.wizard.tables.pendingDescription')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={() => setQueuedExit(null)}>
                        {i18n.t('tableMaps.wizard.cancel')}
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => wizardDispatch({ type: 'DISCARD_PENDING_TABLE' })}
                    >
                        {i18n.t('tableMaps.wizard.draw.discard')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canApplyPendingTable(draft)}
                        onClick={() => wizardDispatch({ type: 'APPLY_PENDING_TABLE' })}
                    >
                        {i18n.t('tableMaps.wizard.features.applyChanges')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Prompt
                when={hasUnsavedChanges}
                message={() => isLeavingRef.current || i18n.t('tableMaps.wizard.unsavedChanges')}
            />
        </div>
    );
}

export default function CreateTableMapWizard() {
    const { id } = useParams<{ id?: string }>();
    const dispatch = useDispatch();
    const details = useSelector((state: ApplicationState) =>
        id ? state.tableMaps.details[id] : undefined,
    );
    const [fetchDone, setFetchDone] = useState<boolean>(!id);
    const [fetchAttempt, setFetchAttempt] = useState(0);

    useEffect(() => {
        if (id) {
            const thunk = tableMapsStore.actionCreators.getTableMapDetails(id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (dispatch(thunk as any) as Promise<void>).then(() => setFetchDone(true));
        }
    }, [id, dispatch, fetchAttempt]);

    // A draft's own tables reserve nothing, so its codes are checked against the server before the
    // editor opens: the result is part of the initial draft, not an edit the user has to save or undo.
    const isDraft = fetchDone && !!details?.isDraft;
    const [draftQrCodes, setDraftQrCodes] = useState<DineInQrCode[] | null>(null);
    useEffect(() => {
        if (!isDraft) return;
        TableMaps.getDineInQrCodes()
            .then(setDraftQrCodes)
            .catch(() => setDraftQrCodes([]));
    }, [isDraft]);
    const released = useMemo(
        () => (isDraft && details ? releaseUsedQrCodes(details.tables, draftQrCodes ?? []) : null),
        [isDraft, details, draftQrCodes],
    );

    function handleRetry() {
        setFetchDone(false);
        setFetchAttempt((n) => n + 1);
    }

    if (id) {
        if (!fetchDone || (isDraft && draftQrCodes === null)) {
            return (
                <Box
                    display="flex"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={theme.palette.common.white}
                >
                    <CircularProgress />
                </Box>
            );
        }

        if (!details) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor={theme.palette.common.white}
                >
                    <Typography style={{ marginBottom: 16 }}>
                        {i18n.t('tableMaps.wizard.loadError')}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleRetry}>
                        {i18n.t('tableMaps.wizard.retry')}
                    </Button>
                </Box>
            );
        }

        return (
            <TableMapWizardProvider
                initialDraft={draftFromDetails(
                    released ? { ...details, tables: released.tables } : details,
                )}
            >
                <WizardShell
                    isEdit={true}
                    editId={id}
                    originalDetails={details}
                    releasedQrTables={released?.releasedLabels ?? []}
                />
            </TableMapWizardProvider>
        );
    }

    return (
        <TableMapWizardProvider>
            <WizardShell isEdit={false} />
        </TableMapWizardProvider>
    );
}
